import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import {
  Loader2, RefreshCw, Plus, ClipboardCheck, ChevronRight, Search,
  AlertCircle, CheckCircle2, Clock,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  SCHEDULED: "Scheduled",
  IN_PROGRESS: "Inspection In Progress",
  AWAITING_REVIEW: "Awaiting Review",
  COMPLETED: "Completed",
  ACTION_REQUIRED: "Action Required",
};
const RATING_TILE = {
  GREEN: { bg: "bg-[#064E2E]/10", fg: "text-[#064E2E]", border: "border-[#064E2E]/30", label: "Good Condition" },
  AMBER: { bg: "bg-[#B88A2A]/10", fg: "text-[#B88A2A]", border: "border-[#B88A2A]/30", label: "Monitor" },
  RED:   { bg: "bg-[#B91C1C]/10", fg: "text-[#B91C1C]", border: "border-[#B91C1C]/30", label: "Action Required" },
};
const STATUSES = ["", "SCHEDULED", "IN_PROGRESS", "AWAITING_REVIEW", "COMPLETED", "ACTION_REQUIRED"];

export default function HealthChecks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/health-checks", { params: statusFilter ? { status: statusFilter } : {} });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? items.filter((h) => `${h.member_name} ${h.park_name} ${h.property_address}`.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div data-testid="health-checks-page">
      <div className="admin-page-header flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Member Benefit · Recurring</p>
          <h1>Annual Health Checks</h1>
          <p className="lead">Schedule, run and publish each member&apos;s annual external Park Home Health Check.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]"/>
            <input className="field-input h-10 pl-9 w-60" placeholder="Search member, park…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="hc-search"/>
          </div>
          <select className="field-input h-10" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="hc-status-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s ? STATUS_LABELS[s] : "All statuses"}</option>)}
          </select>
          <button onClick={() => setShowSchedule(true)} className="text-sm h-10 px-4 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55] inline-flex items-center gap-1.5" data-testid="hc-schedule-btn">
            <Plus size={14}/> Schedule
          </button>
          <button onClick={load} className="btn-outline h-10 py-0 px-3" data-testid="hc-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/></button>
        </div>
      </div>

      <div className="admin-panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.15em]">
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Property</th>
              <th className="px-4 py-3">Inspection date</th>
              <th className="px-4 py-3">Inspector</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Overall</th>
              <th className="px-4 py-3 text-right">Open</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={16}/></td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#475569]">
                <ClipboardCheck size={20} className="inline mb-2 text-[#94A3B8]"/>
                <p>No health checks yet. Schedule the first one to begin.</p>
              </td></tr>
            )}
            {filtered.map((h) => (
              <tr key={h.id} className="border-t border-[#E2DCD0]" data-testid={`hc-row-${h.id}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-[#071A33]">{h.member_name}</p>
                  <p className="text-xs text-[#475569]">{h.member_email}</p>
                </td>
                <td className="px-4 py-3 text-[#475569]">
                  <p>{h.park_name || "—"} {h.plot_number ? `· Plot ${h.plot_number}` : ""}</p>
                  <p className="text-xs truncate max-w-xs">{h.property_address}</p>
                </td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(h.inspection_date)}</td>
                <td className="px-4 py-3 text-[#475569]">{h.inspector_name || "—"}</td>
                <td className="px-4 py-3"><StatusPill status={h.status}/></td>
                <td className="px-4 py-3"><RatingDot rating={h.overall_rating}/></td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/adminpanel/health-checks/${h.id}`} className="text-[#064E2E] text-xs font-medium hover:underline inline-flex items-center gap-1" data-testid={`hc-open-${h.id}`}>
                    Open <ChevronRight size={12}/>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showSchedule && (
        <ScheduleDialog onClose={() => setShowSchedule(false)} onSaved={() => { setShowSchedule(false); load(); }} />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    SCHEDULED:        { bg: "bg-[#0F766E]/10", fg: "text-[#0F766E]", icon: <Clock size={11}/> },
    IN_PROGRESS:      { bg: "bg-[#B88A2A]/10", fg: "text-[#B88A2A]", icon: <Clock size={11}/> },
    AWAITING_REVIEW:  { bg: "bg-[#3B5BDB]/10", fg: "text-[#3B5BDB]", icon: <Clock size={11}/> },
    COMPLETED:        { bg: "bg-[#064E2E]/10", fg: "text-[#064E2E]", icon: <CheckCircle2 size={11}/> },
    ACTION_REQUIRED:  { bg: "bg-[#B91C1C]/10", fg: "text-[#B91C1C]", icon: <AlertCircle size={11}/> },
  };
  const s = map[status] || { bg: "bg-[#94A3B8]/10", fg: "text-[#94A3B8]", icon: null };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase font-semibold px-2 py-1 rounded ${s.bg} ${s.fg}`}>
      {s.icon}{STATUS_LABELS[status] || status}
    </span>
  );
}

function RatingDot({ rating }) {
  if (!rating) return <span className="text-[#94A3B8] text-xs">—</span>;
  const t = RATING_TILE[rating];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${t.fg}`}>
      <span className={`w-2.5 h-2.5 rounded-full ${rating === "GREEN" ? "bg-[#064E2E]" : rating === "AMBER" ? "bg-[#B88A2A]" : "bg-[#B91C1C]"}`}/>
      {rating}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

function ScheduleDialog({ onClose, onSaved }) {
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [inspectionDate, setInspectionDate] = useState("");
  const [inspectorName, setInspectorName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.get("/members").then(({ data }) => setMembers(data.items || []));
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!memberId) { toast.error("Choose a member"); return; }
    setBusy(true);
    try {
      await adminApi.post("/health-checks", {
        member_id: memberId,
        inspection_date: inspectionDate || null,
        inspector_name: inspectorName,
      });
      toast.success("Health Check scheduled");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to schedule");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="hc-schedule-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <form onSubmit={submit} className="relative bg-white rounded-xl border border-[#E2DCD0] shadow-2xl w-full max-w-md p-6">
        <h3 className="font-display text-xl text-[#071A33]">Schedule Annual Health Check</h3>
        <p className="text-xs text-[#475569] mt-1">A blank inspection record will be created and ready to fill in.</p>
        <label className="field-label mt-5">Member</label>
        <select className="field-input" value={memberId} onChange={(e) => setMemberId(e.target.value)} data-testid="hc-schedule-member">
          <option value="">Select a member…</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.full_name} · {m.park_name || "—"}</option>)}
        </select>
        <label className="field-label mt-4">Inspection date (optional)</label>
        <input type="date" className="field-input" value={inspectionDate} onChange={(e) => setInspectionDate(e.target.value)} data-testid="hc-schedule-date"/>
        <label className="field-label mt-4">Inspector name (optional)</label>
        <input className="field-input" value={inspectorName} onChange={(e) => setInspectorName(e.target.value)} placeholder="e.g. Mark Thompson" data-testid="hc-schedule-inspector"/>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-[#475569] hover:bg-[#F7F4ED] rounded-md">Cancel</button>
          <button disabled={busy} className="btn-primary" data-testid="hc-schedule-submit">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Schedule
          </button>
        </div>
      </form>
    </div>
  );
}
